import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useFlipFixedList } from '../../../components/useFlipFixedList';
import {
  IonAlert,
  IonToast,
  IonBadge,
  IonButton,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToggle,
  IonToolbar,
  useIonRouter,
} from '@ionic/react';
import { useLocation } from 'react-router-dom';
import {
  addOutline,
  callOutline,
  chevronDownOutline,
  notificationsOutline,
  peopleOutline,
  refreshOutline,
  settingsOutline,
  shieldCheckmarkOutline,
  trashOutline,
  mailOutline,
  keyOutline,
  timeOutline,
} from 'ionicons/icons';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, onSnapshot } from 'firebase/firestore';
import { useAuth } from '../../../contexts';
import { dbRsRoles } from '../../../auth/firebase';
import { rsAuth } from '../../../services/rsAuthClient';
import { VERIFICATION_CONFIG } from '@rsmaids/shared/constants/auth';
import { canAssignRole, canManageAccount } from '@rsmaids/shared/constants/roles';
import { StaffCacheService, type CachedStaffMember } from '@rsmaids/shared/services/api/staffCacheService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import GlassPicker from '../../../components/GlassPicker';
import styles from './EmployeeManagementDesktop.module.scss';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { translateDynamic } from '../../../i18n/dynamic';

type ToolView = 'employees' | 'phoneAgents';
type SectionKey =
  | 'invite'
  | 'codes'
  | 'notifications'
  | 'roles'
  | 'addStaff'
  | 'resources'
  | 'addAgent'
  | 'agentList';

interface EmployeeCode {
  code: string;
  email: string;
  role: string;
  createdAt: Date;
  expiresAt: Date;
}

interface NotificationCategories {
  bookings: boolean;
  payments: boolean;
  team: boolean;
  forms: boolean;
  overdue: boolean;
  invoice: boolean;
}

interface EmployeeRecord {
  id: string;
  staffId: string;
  name: string;
  email: string;
  role: string;
  notificationsEnabled: boolean;
  notificationCategories?: NotificationCategories;
}

interface ResourceRecord {
  _id: string;
  name: string;
  description?: string;
  type?: string;
}

// Wix groups resources under named categories (its dashboard calls these
// "categories" — e.g. "Team Member 01") via a separate ResourceType record;
// resources.getResourcesGroupedByCategory() already does the join, we just
// need to render its shape instead of a flat list.
type GroupedResources = Record<string, ResourceRecord[]>;

interface Agent {
  sid: string;
  name: string;
  phone: string | null;
  available: boolean;
  activityName: string;
}

const roleOptions = Object.entries(VERIFICATION_CONFIG.ASSIGNABLE_ROLES).map(([label, value]) => ({
  label: label.charAt(0) + label.slice(1).toLowerCase().replace('_', ' '),
  value,
}));

// Custom role dropdown — matches the contact-lookup dark styled dropdown
// Renders the list via portal so it floats over overflow:hidden panels
interface RoleSelectProps {
  options?: { label: string; value: string }[]; // defaults to every assignable role
  value: string;
  onChange: (v: string) => void;
  className?: string;
  placeholder?: string;
  cancelLabel?: string;
  onCancel?: () => void;
}
const RoleSelect: React.FC<RoleSelectProps> = ({ value, onChange, className, placeholder, cancelLabel, onCancel, options = roleOptions }) => {
  const [open, setOpen] = useState(false);
  const [listPos, setListPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = !!wrapperRef.current?.contains(target);
      const inList = !!listRef.current?.contains(target);
      if (!inTrigger && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useFlipFixedList(open, triggerRef, listRef);

  const handleOpen = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setListPos({ top: rect.bottom - 2, left: rect.left, width: rect.width });
    }
    setOpen(v => !v);
  };

  const selected = options.find(r => r.value === value);
  const displayLabel = selected?.label || placeholder || value;
  const isPlaceholder = !selected && !!placeholder;

  const list = open ? ReactDOM.createPortal(
    <div
      ref={listRef}
      className={styles.roleDropdownList}
      style={{ top: listPos.top, left: listPos.left, minWidth: listPos.width }}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          type="button"
          className={`${styles.roleDropdownItem} ${opt.value === value ? styles.roleDropdownItemActive : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => { onChange(opt.value); setOpen(false); }}
        >
          {opt.label}
        </button>
      ))}
      {cancelLabel && (
        <button
          type="button"
          className={`${styles.roleDropdownItem} ${styles.roleDropdownCancel}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onCancel?.();
            setOpen(false);
          }}
        >
          {cancelLabel}
        </button>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div ref={wrapperRef} className={`${styles.roleDropdownWrap} ${className || ''}`}>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        className={`${styles.fieldBox} ${styles.roleDropdownTrigger}`}
        onClick={handleOpen}
        onKeyDown={(e) => e.key === 'Enter' && handleOpen()}
      >
        <span className={`${styles.fieldInput} ${isPlaceholder ? styles.fieldInputPlaceholder : ''}`}>{displayLabel}</span>
        <IonIcon icon={chevronDownOutline} className={`${styles.roleDropdownChevron} ${open ? styles.roleDropdownChevronOpen : ''}`} />
      </div>
      {list}
    </div>
  );
};

interface AgentStatusSelectProps {
  available: boolean;
  disabled?: boolean;
  onChange: (nextAvailable: boolean) => void;
}
const AgentStatusSelect: React.FC<AgentStatusSelectProps> = ({ available, disabled = false, onChange }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [listPos, setListPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const inTrigger = !!wrapperRef.current?.contains(target);
      const inList = !!listRef.current?.contains(target);
      if (!inTrigger && !inList) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useFlipFixedList(open, triggerRef, listRef);

  const handleOpen = () => {
    if (disabled) return;
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setListPos({ top: rect.bottom - 2, left: rect.left, width: rect.width });
    }
    setOpen(v => !v);
  };

  const handleSelect = (nextAvailable: boolean) => {
    onChange(nextAvailable);
    setOpen(false);
  };

  const list = open ? ReactDOM.createPortal(
    <div
      ref={listRef}
      className={styles.agentStatusDropdownList}
      style={{ top: listPos.top, left: listPos.left, minWidth: listPos.width }}
    >
      <button
        type="button"
        className={`${styles.agentStatusDropdownItem} ${styles.agentStatusDropdownItemAvailable} ${available ? styles.agentStatusDropdownItemAvailableActive : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleSelect(true)}
      >{t('desktop.pages.settings.EmployeeManagementDesktop.available')}
      </button>
      <button
        type="button"
        className={`${styles.agentStatusDropdownItem} ${styles.agentStatusDropdownItemUnavailable} ${!available ? styles.agentStatusDropdownItemUnavailableActive : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleSelect(false)}
      >{t('desktop.pages.settings.EmployeeManagementDesktop.unavailable')}
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div ref={wrapperRef} className={styles.agentStatusWrap}>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={`${styles.agentStatusBadge} ${available ? styles.agentStatusAvailable : styles.agentStatusUnavailable} ${styles.agentStatusTrigger} ${disabled ? styles.agentStatusDisabled : ''}`}
        onClick={handleOpen}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleOpen();
          }
        }}
        aria-disabled={disabled}
      >
        <IonIcon icon={timeOutline} />
        <span className={styles.agentStatusLabel}>{available ? t('desktop.pages.settings.EmployeeManagementDesktop.available') : t('desktop.pages.settings.EmployeeManagementDesktop.unavailable')}</span>
        <IonIcon icon={chevronDownOutline} className={`${styles.agentStatusChevron} ${open ? styles.roleDropdownChevronOpen : ''}`} />
      </div>
      {list}
    </div>
  );
};

const getRoleTone = (role: string) => {
  const key = (role || '').toLowerCase();
  if (key.includes('owner') || key.includes('admin')) return styles.roleDanger;
  if (key.includes('manager')) return styles.rolePrimary;
  if (key.includes('lead')) return styles.roleWarning;
  return styles.roleMedium;
};

const formatExpiry = (expiresAt: Date) => {
  const diffMs = expiresAt.getTime() - Date.now();
  if (diffMs <= 0) return i18n.t('desktop.pages.settings.EmployeeManagementDesktop.expired');
  const totalMins = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return hours > 0 ? `${hours}h ${mins}m remaining` : `${mins}m remaining`;
};

const getStaffName = (staff: CachedStaffMember): string => {
  const name = (staff as unknown as { name?: string }).name;
  const first = (staff as unknown as { firstName?: string }).firstName;
  const last = (staff as unknown as { lastName?: string }).lastName;
  const email = (staff as unknown as { email?: string }).email;
  return name || [first, last].filter(Boolean).join(' ').trim() || email || i18n.t('desktop.pages.settings.EmployeeManagementDesktop.staffMember');
};

// i18n-ignore: CSS class names, not text
const TOAST_CLASS_OK = 'app-toast';
// i18n-ignore: CSS class names, not text
const TOAST_CLASS_ERROR = 'app-toast app-toast--error';

const EmployeeManagementDesktop: React.FC = () => {
  const { t } = useTranslation();
  const router = useIonRouter();
  const location = useLocation();
  const { authState } = useAuth();
  const user = authState.user;
  // Offer only the roles this person is allowed to hand out (the server enforces the same rule)
  const assignableRoleOptions = roleOptions.filter(o => canAssignRole(user?.role, o.value));
  const functions = getFunctions();

  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [codes, setCodes] = useState<EmployeeCode[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [resources, setResources] = useState<GroupedResources>({});
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState(''); // why the server refused the invitation (e.g. the email already has an account)
  const [inviteSuccess, setInviteSuccess] = useState(''); // also used for the "staff member deleted" message: both show in the same toast
  const [isDeletingStaff, setIsDeletingStaff] = useState(false);
  const [newStaffFirstName, setNewStaffFirstName] = useState('');
  const [newStaffLastName, setNewStaffLastName] = useState('');
  const [newStaffEmail, setNewStaffEmail] = useState('');
  const [newStaffPhone, setNewStaffPhone] = useState('');
  const [staffExistsInlineMessage, setStaffExistsInlineMessage] = useState('');
  const [isSavingStaff, setIsSavingStaff] = useState(false);
  const [newResourceName, setNewResourceName] = useState('');
  const [newResourceDescription, setNewResourceDescription] = useState('');
  const [isSavingResource, setIsSavingResource] = useState(false);
  const [roleEdits, setRoleEdits] = useState<Record<string, string>>({});
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);
  const [deleteStaff, setDeleteStaff] = useState<EmployeeRecord | null>(null);
  const [deleteResource, setDeleteResource] = useState<ResourceRecord | null>(null);
  const [newAgentName, setNewAgentName] = useState('');
  const [newAgentPhone, setNewAgentPhone] = useState('');
  const [isSavingAgent, setIsSavingAgent] = useState(false);
  const [deleteCode, setDeleteCode] = useState<string | null>(null);
  const [deleteAgent, setDeleteAgent] = useState<Agent | null>(null);
  const [panelLoading, setPanelLoading] = useState(false);
  const [categoriesEmployee, setCategoriesEmployee] = useState<EmployeeRecord | null>(null);
  const [draftCategories, setDraftCategories] = useState<NotificationCategories>({ bookings: true, payments: true, team: true, forms: true, overdue: true, invoice: true });
  const [, setSavingCategories] = useState(false);
  const forceStaffReloadRef = useRef(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<SectionKey, boolean>>({
    invite: false,
    codes: true,
    notifications: true,
    roles: true,
    addStaff: true,
    resources: true,
    addAgent: true,
    agentList: true,
  });

  const activeView: ToolView = useMemo(() => {
    return location.pathname.includes('/phone-agents') ? 'phoneAgents' : 'employees';
  }, [location.pathname]);

  // Expand the section named in the URL's ?section= param — computed
  // during render (React's "adjust state when a dependency changes"
  // pattern) rather than as a setState call inside an effect.
  const [prevLocationSearch, setPrevLocationSearch] = useState(location.search);
  if (prevLocationSearch !== location.search) {
    setPrevLocationSearch(location.search);
    const params = new URLSearchParams(location.search);
    const section = params.get('section') as SectionKey | null;
    if (section) {
      setCollapsedSections(prev => ({ ...prev, [section]: false }));
    }
  }

  const listRoleCodes = useCallback(async () => {
    const res = await rsAuth.listRoleCodes();
    if (!res.success || !res.codes) return [];
    return res.codes.map((code: EmployeeCode) => ({
      ...code,
      createdAt: new Date(code.createdAt),
      expiresAt: new Date(code.expiresAt),
    }));
  }, []);

  const listEmployees = useCallback(async () => {
    const staffCache = StaffCacheService.getInstance();
    const forceReload = forceStaffReloadRef.current;
    forceStaffReloadRef.current = false;
    await staffCache.loadStaffCache(forceReload);
    const staff = staffCache.getAllStaff();
    const getStatus = httpsCallable(functions, 'getEmployeeNotificationStatus');

    const currentUserContactId = user?.wixContactId || '';

    const filteredStaff = staff.filter((member) => {
      const rawMember = member as unknown as {
        email?: string;
        contactId?: string;
        associatedWixIdentity?: { identificationData?: { contactId?: string } };
      };
      const memberContactId = rawMember.contactId
        || rawMember.associatedWixIdentity?.identificationData?.contactId
        || '';

      if (currentUserContactId && memberContactId === currentUserContactId) {
        return false;
      }

      return true;
    });

    const mapped = await Promise.all(filteredStaff.map(async (member) => {
      const m = member as unknown as { id?: string; staffMemberId?: string; _id?: string; email?: string; role?: string };
      const email = m.email || '';
      let notificationsEnabled = false;
      let resolvedRole = m.role || 'staff';

      if (email) {
        try {
          const result = await getStatus({ email });
          const data = result.data as { notificationsEnabled?: boolean; role?: string; preferences?: { categories?: NotificationCategories } };
          if (typeof data.notificationsEnabled === 'boolean') {
            notificationsEnabled = data.notificationsEnabled;
          }
          if (data.role && typeof data.role === 'string') {
            resolvedRole = data.role;
          }
          if (data.preferences?.categories) {
            (member as unknown as Record<string, unknown>)._notifCategories = data.preferences.categories;
          }
        } catch {
          notificationsEnabled = false;
        }
      }

      return {
        id: m.staffMemberId || m.id || m._id || email,
        staffId: m.staffMemberId || m.id || m._id || '',
        name: getStaffName(member),
        email,
        role: resolvedRole,
        notificationsEnabled,
        notificationCategories: (member as unknown as Record<string, unknown>)._notifCategories as NotificationCategories | undefined,
      } as EmployeeRecord;
    }));

    return mapped.sort((a, b) => a.name.localeCompare(b.name));
  }, [functions, user?.wixContactId]);

  const listAgents = useCallback(async () => {
    const fn = httpsCallable(functions, 'listAgents');
    const result = await fn({});
    const data = result.data as { agents?: Agent[] };
    return data.agents || [];
  }, [functions]);

  const listResources = useCallback(async () => {
    const service = DataServiceFactory.getService();
    const grouped = await service.resources.getResourcesGroupedByCategory();
    return grouped as unknown as GroupedResources;
  }, []);

  const syncStaffCache = useCallback(async (): Promise<boolean> => {
    try {
      const syncStaffCacheFunction = httpsCallable<void, { success: boolean; message: string; count?: number }>(
        functions,
        'syncStaffCacheEndpoint'
      );
      await syncStaffCacheFunction();

      forceStaffReloadRef.current = true;
      const freshEmployees = await listEmployees();
      setEmployees(freshEmployees);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
      return true;
    } catch (error) {
      console.error('Error syncing staff cache:', error);
      return false;
    }
  }, [functions, listEmployees]);

  const refreshAll = useCallback(async () => {
    // Only ever called once, from the mount effect below — loading already
    // starts at its "loading" default (useState(true)), so no need to set
    // it again before the fetch.
    try {
      forceStaffReloadRef.current = true;
      const [freshCodes, freshEmployees, freshAgents] = await Promise.all([
        listRoleCodes(),
        listEmployees(),
        listAgents(),
      ]);
      const freshResources = await listResources();
      setCodes(freshCodes);
      setEmployees(freshEmployees);
      setAgents(freshAgents);
      setResources(freshResources);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
    } finally {
      setLoading(false);
    }
  }, [listRoleCodes, listEmployees, listAgents, listResources]);

  const refreshEmployeesSilently = useCallback(async () => {
    forceStaffReloadRef.current = true;
    const freshEmployees = await listEmployees();
    setEmployees(freshEmployees);
    setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
  }, [listEmployees]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!user) return;

    let inFlight = false;
    let queued = false;

    const queueSilentRefresh = () => {
      if (inFlight) {
        queued = true;
        return;
      }

      inFlight = true;
      void (async () => {
        try {
          await refreshEmployeesSilently();
        } finally {
          inFlight = false;
          if (queued) {
            queued = false;
            queueSilentRefresh();
          }
        }
      })();
    };

    const unsubscribeStaff = onSnapshot(
      collection(dbRsRoles, 'staff'),
      queueSilentRefresh,
      (error) => {
        console.warn('Silent staff refresh listener error:', error);
      }
    );

    return () => {
      unsubscribeStaff();
    };
  }, [user, refreshEmployeesSilently]);

  const goToSettings = () => {
    router.push('/tabs/settings', 'root');
  };

  const toggleSection = (key: SectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim() || !inviteRole) return;
    setIsInviting(true);
    try {
      const invitedEmail = inviteEmail.trim();
      const result = await rsAuth.createEmployeeCode(invitedEmail, inviteRole);
      if (!result.success) setInviteError((typeof result.error === 'string' ? result.error : result.error?.message) || t('desktop.pages.settings.EmployeeManagementDesktop.couldNotSendTheInvitation'));
      if (result.success) {
        setInviteSuccess(t('desktop.pages.settings.EmployeeManagementDesktop.invitationSentTo', { invitedEmail }));
        setInviteEmail('');
        const freshCodes = await listRoleCodes();
        setCodes(freshCodes);
      }
    } finally {
      setIsInviting(false);
    }
  };

  const openCategoriesModal = (employee: EmployeeRecord) => {
    setCategoriesEmployee(employee);
    setDraftCategories({
      bookings: employee.notificationCategories?.bookings ?? true,
      payments: employee.notificationCategories?.payments ?? true,
      team: employee.notificationCategories?.team ?? true,
      forms: employee.notificationCategories?.forms ?? true,
      overdue: employee.notificationCategories?.overdue ?? true,
      invoice: employee.notificationCategories?.invoice ?? true,
    });
  };

  const handleSaveCategories = async () => {
    if (!categoriesEmployee?.email) return;
    setSavingCategories(true);
    try {
      const fn = httpsCallable(functions, 'updateEmployeeNotificationCategories');
      await fn({ email: categoriesEmployee.email, categories: draftCategories });
      setEmployees((prev) => prev.map((e) =>
        e.id === categoriesEmployee.id ? { ...e, notificationCategories: { ...draftCategories } } : e
      ));
      setCategoriesEmployee(null);
    } finally {
      setSavingCategories(false);
    }
  };

  const handleToggleNotifications = async (employee: EmployeeRecord) => {
    if (!employee.email) return;
    setPanelLoading(true);
    try {
      const next = !employee.notificationsEnabled;
      const result = await rsAuth.updateEmployeeNotifications(employee.email, next);
      if (result.success) {
        setEmployees((prev) => prev.map((entry) => entry.id === employee.id ? { ...entry, notificationsEnabled: next } : entry));
      }
    } finally {
      setPanelLoading(false);
    }
  };

  const handleAddStaff = async () => {
    setStaffExistsInlineMessage('');

    if (!newStaffFirstName.trim() || !newStaffLastName.trim() || !newStaffEmail.trim()) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newStaffEmail.trim())) {
      return;
    }

    setIsSavingStaff(true);
    try {
      const dataService = DataServiceFactory.getService();
      const normalizedEmail = newStaffEmail.trim().toLowerCase();
      const normalizedName = `${newStaffFirstName} ${newStaffLastName}`.trim().toLowerCase();

      const existingLocalByEmail = employees.some((employee) =>
        (employee.email || '').trim().toLowerCase() === normalizedEmail
      );
      const existingLocalByNameNoEmail = employees.some((employee) =>
        (employee.name || '').trim().toLowerCase() === normalizedName && !(employee.email || '').trim()
      );

      if (existingLocalByEmail || existingLocalByNameNoEmail) {
        console.warn('Duplicate staff prevented from local state:', { normalizedEmail, normalizedName });
        setStaffExistsInlineMessage(t('desktop.pages.settings.EmployeeManagementDesktop.staffMemberAlreadyExistsCheckExisting'));
        return;
      }

      const existingStaff = await dataService.staff.getAllStaffMembers();
      const duplicateByEmail = existingStaff.find((staffMember) => {
        const staffEmail = (staffMember.email || '').trim().toLowerCase();
        return staffEmail !== '' && staffEmail === normalizedEmail;
      });

      const duplicateByNameNoEmail = existingStaff.find((staffMember) => {
        const staffEmail = (staffMember.email || '').trim().toLowerCase();
        const staffName = (staffMember.name || '').trim().toLowerCase();
        return staffEmail === '' && staffName !== '' && staffName === normalizedName;
      });

      if (duplicateByEmail || duplicateByNameNoEmail) {
        console.warn('Duplicate staff prevented from Wix lookup:', { normalizedEmail, normalizedName });
        setStaffExistsInlineMessage(t('desktop.pages.settings.EmployeeManagementDesktop.staffMemberAlreadyExistsInWix'));
        return;
      }

      const newStaff = await dataService.staff.createStaffMember({
        name: `${newStaffFirstName} ${newStaffLastName}`.trim(),
        email: newStaffEmail.trim(),
        phone: newStaffPhone.trim() || '',
        resourceId: '',
      });

      if (newStaff) {
        const synced = await syncStaffCache();
        if (!synced) {
          console.warn('Staff was created in Wix, but cache sync failed. Avoid retrying create to prevent duplicates.');

          const createdStaff = newStaff as unknown as { _id?: string; id?: string; staffMemberId?: string; name?: string; email?: string };
          const createdStaffId = createdStaff.staffMemberId || createdStaff._id || createdStaff.id || '';
          const createdName = (createdStaff.name || `${newStaffFirstName} ${newStaffLastName}`).trim() || 'Staff Member';
          const createdEmail = (createdStaff.email || newStaffEmail).trim();

          if (createdStaffId || createdEmail) {
            setEmployees((prev) => {
              const alreadyPresent = prev.some((entry) =>
                (createdStaffId && entry.staffId === createdStaffId) ||
                (createdEmail && entry.email.trim().toLowerCase() === createdEmail.toLowerCase())
              );

              if (alreadyPresent) {
                return prev;
              }

              const optimisticEntry: EmployeeRecord = {
                id: createdStaffId || createdEmail,
                staffId: createdStaffId,
                name: createdName,
                email: createdEmail,
                role: 'staff',
                notificationsEnabled: false,
              };

              return [...prev, optimisticEntry].sort((a, b) => a.name.localeCompare(b.name));
            });

            setRoleEdits((prev) => ({
              ...prev,
              [createdStaffId || createdEmail]: 'staff',
            }));
          }
        }

        setNewStaffFirstName('');
        setNewStaffLastName('');
        setNewStaffEmail('');
        setNewStaffPhone('');
        setStaffExistsInlineMessage('');
      }
    } catch (error) {
      console.error('Error creating staff member:', error);
    } finally {
      setIsSavingStaff(false);
    }
  };

  const handleDeleteStaff = async () => {
    if (!deleteStaff?.staffId) return;
    const staffId = deleteStaff.staffId;
    setIsDeletingStaff(true);
    setPanelLoading(true);
    try {
      const service = DataServiceFactory.getService();
      await service.staff.deleteStaffMember(staffId);
      await syncStaffCache();
      const freshEmployees = await listEmployees();
      setEmployees(freshEmployees);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
      setInviteSuccess(t('desktop.pages.settings.EmployeeManagementDesktop.staffMemberDeleted'));
    } catch (error) {
      console.error('Error deleting staff member:', error);
      setInviteError(t('desktop.pages.settings.EmployeeManagementDesktop.couldNotDeleteTheStaffMember'));
    } finally {
      setDeleteStaff(null);
      setIsDeletingStaff(false);
      setPanelLoading(false);
    }
  };

  const handleSaveRole = async (employee: EmployeeRecord) => {
    const nextRole = roleEdits[employee.id] || employee.role;
    if (!employee.staffId || !nextRole || nextRole === employee.role) return;

    setSavingRoleId(employee.id);
    try {
      const result = await rsAuth.updateEmployeeNotifications(
        employee.email || '',
        employee.notificationsEnabled,
        nextRole,
        employee.staffId
      );
      if (!result.success) {
        throw new Error(result.message || t('desktop.pages.settings.EmployeeManagementDesktop.failedToUpdateRoleInFirebase'));
      }

      forceStaffReloadRef.current = true;
      const freshEmployees = await listEmployees();
      setEmployees(freshEmployees);
      setRoleEdits(Object.fromEntries(freshEmployees.map((e) => [e.id, e.role])));
    } catch (error) {
      console.error('Failed to save role in Firebase:', error);
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleRevokeCode = async () => {
    if (!deleteCode) return;
    setPanelLoading(true);
    try {
      const result = await rsAuth.revokeRoleCode(deleteCode);
      if (result.success) {
        setCodes((prev) => prev.filter((entry) => entry.code !== deleteCode));
      }
    } finally {
      setDeleteCode(null);
      setPanelLoading(false);
    }
  };

  const handleAddAgent = async () => {
    if (!newAgentName.trim() || !newAgentPhone.trim()) return;
    setIsSavingAgent(true);
    try {
      const fn = httpsCallable(functions, 'addAgent');
      await fn({ name: newAgentName.trim(), phone: newAgentPhone.trim() });
      setNewAgentName('');
      setNewAgentPhone('');
      setAgents(await listAgents());
    } finally {
      setIsSavingAgent(false);
    }
  };

  const handleCreateResource = async () => {
    if (!newResourceName.trim()) return;

    setIsSavingResource(true);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.createResource({
        name: newResourceName.trim(),
        description: newResourceDescription.trim() || '',
        type: 'RESOURCE',
      });
      setResources(await listResources());
      setNewResourceName('');
      setNewResourceDescription('');
    } finally {
      setIsSavingResource(false);
    }
  };

  const handleDeleteResource = async () => {
    if (!deleteResource?._id) return;

    setPanelLoading(true);
    try {
      const service = DataServiceFactory.getService();
      await service.resources.deleteResource(deleteResource._id);
      setResources(await listResources());
    } finally {
      setDeleteResource(null);
      setPanelLoading(false);
    }
  };

  const handleToggleAgent = async (agent: Agent, nextAvailable: boolean) => {
    if (agent.available === nextAvailable) return;
    if (!agent.sid) return;
    setPanelLoading(true);
    try {
      const fn = httpsCallable(functions, 'setAgentAvailability');
      await fn({ workerSid: agent.sid, sid: agent.sid, available: nextAvailable });
      setAgents((prev) => prev.map((entry) => entry.sid === agent.sid ? { ...entry, available: nextAvailable } : entry));
    } finally {
      setPanelLoading(false);
    }
  };

  const handleDeleteAgent = async () => {
    if (!deleteAgent) return;
    if (!deleteAgent.sid) return;
    setPanelLoading(true);
    try {
      const fn = httpsCallable(functions, 'removeAgent');
      await fn({ workerSid: deleteAgent.sid, sid: deleteAgent.sid });
      setAgents((prev) => prev.filter((entry) => entry.sid !== deleteAgent.sid));
    } finally {
      setDeleteAgent(null);
      setPanelLoading(false);
    }
  };

  const activeEmployees = employees.length;
  const activeCodes = codes.filter((entry) => entry.expiresAt.getTime() > new Date().getTime()).length;
  const enabledNotifications = employees.filter((entry) => entry.notificationsEnabled).length;
  const availableAgents = agents.filter((entry) => entry.available).length;

  // Wix Bookings auto-creates an uncategorized Resource entry for every
  // staff member (for scheduling) — these land specifically in "Unassigned"
  // as confusing duplicates of people already listed above in the staff
  // sections. Real resources that happen to share a staff member's name
  // (e.g. equipment assigned per-person) are NOT duplicates — they're
  // deliberately filed under a real category, so only filter Unassigned.
  const staffNames = new Set(employees.map((e) => e.name.trim().toLowerCase()).filter(Boolean));
  const displayResources: GroupedResources = Object.fromEntries(
    Object.entries(resources)
      .map(([category, items]) => [
        category,
        category === 'Unassigned'
          ? items.filter((r) => !staffNames.has((r.name || '').trim().toLowerCase()))
          : items,
      ] as [string, ResourceRecord[]])
      .filter(([, items]) => items.length > 0)
  );

  return (
    <IonPage className={styles.page}>
      <IonHeader className={styles.header}>
        <IonToolbar className={styles.toolbar}>
          <IonTitle className={styles.pageTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.employeeManagement')}</IonTitle>
          <div slot="end" className={styles.topBarRight}>
            <IonButton className={styles.actionButton} fill="outline" onClick={refreshAll}>
              <IonIcon icon={refreshOutline} slot="start" />{t('desktop.pages.settings.EmployeeManagementDesktop.refresh')}
            </IonButton>
            <IonButton className={styles.actionButton} fill="outline" onClick={goToSettings}>
              <IonIcon icon={settingsOutline} slot="start" />{t('desktop.pages.settings.EmployeeManagementDesktop.settings')}
            </IonButton>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent className={styles.content}>
        <div className={styles.layout}>
          <div className={styles.statsRow}>
            <div className={styles.statCard}>
              <IonIcon icon={peopleOutline} className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{activeEmployees}</div>
                <div className={styles.statLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.teamMembers')}</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <IonIcon icon={keyOutline} className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{activeCodes}</div>
                <div className={styles.statLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.activeCodes')}</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <IonIcon icon={notificationsOutline} className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{enabledNotifications}</div>
                <div className={styles.statLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.notificationsOn')}</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <IonIcon icon={callOutline} className={styles.statIcon} />
              <div>
                <div className={styles.statValue}>{availableAgents}</div>
                <div className={styles.statLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.agentsAvailable')}</div>
              </div>
            </div>
          </div>

          <div className={styles.body}>
            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <IonIcon icon={shieldCheckmarkOutline} className={styles.panelIcon} />
                <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.workspaceOverview')}</span>
              </div>

              <div className={styles.summaryList}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.currentWorkspace')}</span>
                  <span className={styles.summaryValue}>{activeView === 'employees' ? t('desktop.pages.settings.EmployeeManagementDesktop.employeeManagement') : t('desktop.pages.settings.EmployeeManagementDesktop.phoneAgents')}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.navigation')}</span>
                  <span className={styles.summaryValue}>{t('desktop.pages.settings.EmployeeManagementDesktop.useBusinessToolsInTheDesktop')}</span>
                </div>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>{t('desktop.pages.settings.EmployeeManagementDesktop.quickTip')}</span>
                  <span className={styles.summaryValue}>{t('desktop.pages.settings.EmployeeManagementDesktop.allSectionsCanBeExpandedOr')}</span>
                </div>
              </div>
            </div>

            <div className={styles.rightCol}>
              {loading ? (
                <div className={styles.loadingWrap}><IonSpinner name="bubbles" color="primary" /></div>
              ) : activeView === 'employees' ? (
                <>
                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('invite')}>
                      <IonIcon icon={mailOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.inviteEmployee')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.invite ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.invite && <div className={styles.formRow}>
                      <div className={styles.fieldBox}>
                        <input
                          type="email"
                          value={inviteEmail}
                          placeholder="employee@email.com"
                          className={styles.fieldInput}
                          onChange={(e) => setInviteEmail(e.target.value)}
                        />
                      </div>
                      <RoleSelect
                        value={inviteRole}
                        onChange={setInviteRole}
                        options={assignableRoleOptions}
                        placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.role')}
                        cancelLabel="Cancel"
                        onCancel={() => {
                          setInviteRole('');
                          setInviteEmail('');
                        }}
                      />
                      <IonButton
                        className={`${styles.primaryButton} ${styles.inviteButton}`}
                        onClick={handleCreateInvite}
                        disabled={isInviting || !inviteEmail.trim() || !inviteRole}
                      >
                        <IonIcon icon={addOutline} slot="start" />
                        {isInviting ? t('desktop.pages.settings.EmployeeManagementDesktop.sending') : t('desktop.pages.settings.EmployeeManagementDesktop.sendInvite')}
                      </IonButton>
                    </div>}
                  </div>

                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('codes')}>
                      <IonIcon icon={keyOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.activeInvitationCodes')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.codes ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.codes && <IonList className={styles.list}>
                      {codes.length === 0 ? (
                        <IonItem lines="none"><IonLabel color="medium">{t('desktop.pages.settings.EmployeeManagementDesktop.noActiveInvitationCodes')}</IonLabel></IonItem>
                      ) : codes.map((code) => (
                        <IonItem key={code.code} lines="full" className={styles.listItem}>
                          <IonLabel>
                            <h3>{code.email}</h3>
                            <p>{t('desktop.pages.settings.EmployeeManagementDesktop.code', { code: code.code })} {formatExpiry(code.expiresAt)}</p>
                          </IonLabel>
                          <span className={`${styles.roleBadge} ${getRoleTone(code.role)}`}>{code.role}</span>
                          <IonButton fill="clear" color="danger" onClick={() => setDeleteCode(code.code)}>
                            <IonIcon icon={trashOutline} slot="icon-only" />
                          </IonButton>
                        </IonItem>
                      ))}
                    </IonList>}
                  </div>

                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('notifications')}>
                      <IonIcon icon={notificationsOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.employeeNotificationAccess')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.notifications ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.notifications && <IonList className={styles.list}>
                      {employees.length === 0 ? (
                        <IonItem lines="none"><IonLabel color="medium">{t('desktop.pages.settings.EmployeeManagementDesktop.noStaffRecordsFound')}</IonLabel></IonItem>
                      ) : employees.map((employee) => (
                        <IonItem key={employee.id} lines="full" className={styles.listItem}>
                          <IonLabel>
                            <h3>{employee.name}</h3>
                            <p>{employee.email || t('desktop.pages.settings.EmployeeManagementDesktop.noEmail')} • {employee.role}</p>
                          </IonLabel>
                          {employee.notificationsEnabled && employee.email && (
                            <IonButton
                              fill="outline"
                              size="small"
                              slot="end"
                              className={styles.categoriesBtn}
                              onClick={() => openCategoriesModal(employee)}
                            >{t('desktop.pages.settings.EmployeeManagementDesktop.categories')}
                            </IonButton>
                          )}
                          <IonToggle
                            slot="end"
                            checked={employee.notificationsEnabled}
                            onIonChange={() => handleToggleNotifications(employee)}
                            disabled={panelLoading || !employee.email}
                          />
                        </IonItem>
                      ))}
                    </IonList>}
                  </div>

                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('roles')}>
                      <IonIcon icon={shieldCheckmarkOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.manageStaffRoles')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.roles ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.roles && <IonList className={styles.list}>
                      {employees.length === 0 ? (
                        <IonItem lines="none"><IonLabel color="medium">{t('desktop.pages.settings.EmployeeManagementDesktop.noStaffRecordsFound')}</IonLabel></IonItem>
                      ) : employees.map((employee) => {
                        const selectedRole = roleEdits[employee.id] ?? employee.role;
                        const hasChanged = selectedRole !== employee.role;
                        // An account this person may not manage (e.g. a manager looking at an owner) shows its role but cannot be changed
                        const canEditRole = canManageAccount(user?.role, employee.role);
                        const rowRoleOptions = roleOptions.filter(o => o.value === employee.role || (canEditRole && canAssignRole(user?.role, o.value)));
                        return (
                          <IonItem key={`${employee.id}-role`} lines="full" className={styles.listItem}>
                            <IonLabel>
                              <h3>{employee.name}</h3>
                              <p>{employee.email || t('desktop.pages.settings.EmployeeManagementDesktop.noEmail')}</p>
                            </IonLabel>
                            <RoleSelect
                              value={selectedRole}
                              onChange={(v) => setRoleEdits((prev) => ({ ...prev, [employee.id]: v }))}
                              options={rowRoleOptions}
                              className={styles.roleSelectInline}
                            />
                            <IonButton
                              size="small"
                              className={styles.saveRoleButton}
                              disabled={!canEditRole || !hasChanged || !employee.staffId || savingRoleId === employee.id}
                              onClick={() => handleSaveRole(employee)}
                            >
                              {savingRoleId === employee.id ? t('desktop.pages.settings.EmployeeManagementDesktop.saving') : t('desktop.pages.settings.EmployeeManagementDesktop.save')}
                            </IonButton>
                            <IonButton fill="clear" color="danger" onClick={() => setDeleteStaff(employee)} disabled={!employee.staffId}>
                              <IonIcon icon={trashOutline} slot="icon-only" />
                            </IonButton>
                          </IonItem>
                        );
                      })}
                    </IonList>}
                  </div>

                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('addStaff')}>
                      <IonIcon icon={addOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.addStaffMember')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.addStaff ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.addStaff && <>
                    <div className={styles.formRowTwo}>
                      <div className={styles.fieldBox}><input type="text" value={newStaffFirstName} placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.firstName')} className={styles.fieldInput} onChange={(e) => { setNewStaffFirstName(e.target.value); setStaffExistsInlineMessage(''); }} /></div>
                      <div className={styles.fieldBox}><input type="text" value={newStaffLastName} placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.lastName')} className={styles.fieldInput} onChange={(e) => { setNewStaffLastName(e.target.value); setStaffExistsInlineMessage(''); }} /></div>
                      <div className={styles.fieldBox}><input type="email" value={newStaffEmail} placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.email')} className={styles.fieldInput} onChange={(e) => { setNewStaffEmail(e.target.value); setStaffExistsInlineMessage(''); }} /></div>
                      <div className={styles.fieldBox}><input type="tel" value={newStaffPhone} placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.phoneOptional')} className={styles.fieldInput} onChange={(e) => { setNewStaffPhone(e.target.value); setStaffExistsInlineMessage(''); }} /></div>
                    </div>
                    {staffExistsInlineMessage && (
                      <p style={{ margin: '8px 0 0', padding: '8px 20px', color: 'var(--ion-color-danger-shade)', fontSize: '0.78rem', fontWeight: 600 }}>
                        {staffExistsInlineMessage}
                      </p>
                    )}
                    <div className={styles.formFooter}>
                      <IonButton className={`${styles.primaryButton} ${styles.addStaffButton}`} onClick={handleAddStaff} disabled={isSavingStaff || !newStaffFirstName.trim() || !newStaffLastName.trim() || !newStaffEmail.trim()}>
                        <IonIcon icon={addOutline} slot="start" />
                        {isSavingStaff ? t('desktop.pages.settings.EmployeeManagementDesktop.creating') : t('desktop.pages.settings.EmployeeManagementDesktop.createStaffMember')}
                      </IonButton>
                    </div>
                    </>}
                  </div>

                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('resources')}>
                      <IonIcon icon={keyOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.resources')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.resources ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.resources && <>
                    <div className={styles.formRowTwo}>
                      <div className={styles.fieldBox}><input type="text" value={newResourceName} placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.resourceName')} className={styles.fieldInput} onChange={(e) => setNewResourceName(e.target.value)} /></div>
                      <div className={styles.fieldBox}><input type="text" value={newResourceDescription} placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.descriptionOptional')} className={styles.fieldInput} onChange={(e) => setNewResourceDescription(e.target.value)} /></div>
                    </div>
                    <div className={styles.formFooter}>
                      <IonButton className={`${styles.primaryButton} ${styles.addResourceButton}`} onClick={handleCreateResource} disabled={isSavingResource || !newResourceName.trim()}>
                        <IonIcon icon={addOutline} slot="start" />
                        {isSavingResource ? t('desktop.pages.settings.EmployeeManagementDesktop.adding') : t('desktop.pages.settings.EmployeeManagementDesktop.addResource')}
                      </IonButton>
                    </div>
                    {Object.keys(displayResources).length === 0 ? (
                      <IonList className={styles.list}>
                        <IonItem lines="none"><IonLabel color="medium">{t('desktop.pages.settings.EmployeeManagementDesktop.noResourcesCreatedYet')}</IonLabel></IonItem>
                      </IonList>
                    ) : Object.entries(displayResources).map(([category, items]) => (
                      <IonList key={category} className={styles.list}>
                        <div className={styles.resourceGroupHeader}>
                          <span>{translateDynamic(category)}</span>
                          <IonBadge className={styles.resourceGroupCount}>{items.length}</IonBadge>
                        </div>
                        {items.map((resource) => (
                          <IonItem key={resource._id} lines="full" className={styles.listItem}>
                            <IonLabel>
                              <h3>{resource.name}</h3>
                              {resource.description && <p>{resource.description}</p>}
                            </IonLabel>
                            <IonButton fill="clear" color="danger" onClick={() => setDeleteResource(resource)}>
                              <IonIcon icon={trashOutline} slot="icon-only" />
                            </IonButton>
                          </IonItem>
                        ))}
                      </IonList>
                    ))}
                    </>}
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('addAgent')}>
                      <IonIcon icon={addOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.addPhoneAgent')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.addAgent ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.addAgent && <div className={styles.formRow}>
                      <div className={styles.fieldBox}>
                        <input
                          type="text"
                          value={newAgentName}
                          placeholder={t('desktop.pages.settings.EmployeeManagementDesktop.agentName')}
                          className={styles.fieldInput}
                          onChange={(e) => setNewAgentName(e.target.value)}
                        />
                      </div>
                      <div className={styles.fieldBox}>
                        <input
                          type="tel"
                          value={newAgentPhone}
                          placeholder="+1xxxxxxxxxx"
                          className={styles.fieldInput}
                          onChange={(e) => setNewAgentPhone(e.target.value)}
                        />
                      </div>
                      <IonButton
                        className={`${styles.primaryButton} ${styles.addAgentButton}`}
                        onClick={handleAddAgent}
                        disabled={isSavingAgent || !newAgentName.trim() || !newAgentPhone.trim()}
                      >
                        <IonIcon icon={addOutline} slot="start" />
                        {isSavingAgent ? t('desktop.pages.settings.EmployeeManagementDesktop.adding') : t('desktop.pages.settings.EmployeeManagementDesktop.addAgent')}
                      </IonButton>
                    </div>}
                  </div>

                  <div className={styles.panel}>
                    <button type="button" className={`${styles.panelHeader} ${styles.panelHeaderButton}`} onClick={() => toggleSection('agentList')}>
                      <IonIcon icon={callOutline} className={styles.panelIcon} />
                      <span className={styles.panelTitle}>{t('desktop.pages.settings.EmployeeManagementDesktop.currentPhoneAgents')}</span>
                      <IonIcon icon={chevronDownOutline} className={`${styles.sectionChevron} ${collapsedSections.agentList ? styles.sectionChevronClosed : ''}`} />
                    </button>
                    {!collapsedSections.agentList && <IonList className={styles.list}>
                      {agents.length === 0 ? (
                        <IonItem lines="none"><IonLabel color="medium">{t('desktop.pages.settings.EmployeeManagementDesktop.noPhoneAgentsConfigured')}</IonLabel></IonItem>
                      ) : agents.map((agent) => (
                        <IonItem key={agent.sid} lines="full" className={styles.listItem}>
                          <IonLabel>
                            <h3>{agent.name}</h3>
                            <p>{agent.phone || t('desktop.pages.settings.EmployeeManagementDesktop.noPhone')} • {agent.activityName || t('desktop.pages.settings.EmployeeManagementDesktop.noActivity')}</p>
                          </IonLabel>
                          <AgentStatusSelect
                            available={agent.available}
                            disabled={panelLoading}
                            onChange={(nextAvailable) => void handleToggleAgent(agent, nextAvailable)}
                          />
                          <IonButton fill="clear" color="danger" onClick={() => setDeleteAgent(agent)}>
                            <IonIcon icon={trashOutline} slot="icon-only" />
                          </IonButton>
                        </IonItem>
                      ))}
                    </IonList>}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </IonContent>

      {/* Success/update at the bottom; errors at the top, where they are noticed first */}
      <IonToast isOpen={!!inviteSuccess} message={inviteSuccess} duration={3500} position="bottom" cssClass={TOAST_CLASS_OK} onDidDismiss={() => setInviteSuccess('')} />
      <IonToast isOpen={!!inviteError} message={inviteError} duration={5000} position="top" cssClass={TOAST_CLASS_ERROR} onDidDismiss={() => setInviteError('')} />

      <IonAlert
        isOpen={!!deleteCode}
        cssClass="glass-alert"
        onDidDismiss={() => setDeleteCode(null)}
        header={t('desktop.pages.settings.EmployeeManagementDesktop.revokeInvitation')}
        message={t('desktop.pages.settings.EmployeeManagementDesktop.removeThisEmployeeInviteCode')}
        buttons={[
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.cancel'), role: 'cancel' },
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.revoke'), role: 'destructive', handler: handleRevokeCode },
        ]}
      />

      <IonAlert
        isOpen={!!deleteAgent}
        cssClass="glass-alert"
        onDidDismiss={() => setDeleteAgent(null)}
        header={t('desktop.pages.settings.EmployeeManagementDesktop.removePhoneAgent')}
        message={t('desktop.pages.settings.EmployeeManagementDesktop.removeFromPhoneAgents', { v1: deleteAgent?.name || 'this agent' })}
        buttons={[
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.cancel'), role: 'cancel' },
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.remove'), role: 'destructive', handler: handleDeleteAgent },
        ]}
      />

      <IonAlert
        isOpen={!!deleteStaff}
        cssClass="glass-alert"
        backdropDismiss={!isDeletingStaff}
        onDidDismiss={() => { if (!isDeletingStaff) setDeleteStaff(null); }}
        header={t('desktop.pages.settings.EmployeeManagementDesktop.deleteStaffMember')}
        message={t('desktop.pages.settings.EmployeeManagementDesktop.delete2', { v1: deleteStaff?.name || 'this staff member' })}
        buttons={[
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.cancel'), role: 'cancel', htmlAttributes: { disabled: isDeletingStaff } },
          {
            // The spinner lives in the button itself: the alert stays open (handler returns false) until the delete is finished
            text: isDeletingStaff ? t('desktop.pages.settings.EmployeeManagementDesktop.deleting') : t('desktop.pages.settings.EmployeeManagementDesktop.delete'),
            role: 'destructive',
            cssClass: isDeletingStaff ? 'alert-button-loading' : '', // i18n-ignore: CSS class name
            handler: () => { if (!isDeletingStaff) void handleDeleteStaff(); return false; },
          },
        ]}
      />

      <IonAlert
        isOpen={!!deleteResource}
        cssClass="glass-alert"
        onDidDismiss={() => setDeleteResource(null)}
        header={t('desktop.pages.settings.EmployeeManagementDesktop.deleteResource')}
        message={t('desktop.pages.settings.EmployeeManagementDesktop.delete2', { v1: deleteResource?.name || 'this resource' })}
        buttons={[
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.cancel'), role: 'cancel' },
          { text: t('desktop.pages.settings.EmployeeManagementDesktop.delete'), role: 'destructive', handler: handleDeleteResource },
        ]}
      />

      <GlassPicker
        isOpen={!!categoriesEmployee}
        onClose={() => setCategoriesEmployee(null)}
        title={t('desktop.pages.settings.EmployeeManagementDesktop.notificationCategories')}
        subtitle={categoriesEmployee?.name}
        options={[
          { value: 'bookings', label: t('desktop.pages.settings.EmployeeManagementDesktop.bookings'), sublabel: 'New bookings, cancellations, reschedules' },
          { value: 'payments', label: t('desktop.pages.settings.EmployeeManagementDesktop.payments'), sublabel: 'Payment received and failed' },
          { value: 'team', label: t('desktop.pages.settings.EmployeeManagementDesktop.team'), sublabel: 'Team updates and announcements' },
          { value: 'forms', label: t('desktop.pages.settings.EmployeeManagementDesktop.forms'), sublabel: 'New form submissions' },
          { value: 'overdue', label: t('desktop.pages.settings.EmployeeManagementDesktop.overdue'), sublabel: 'Overdue invoice reminders' },
          { value: 'invoice', label: t('desktop.pages.settings.EmployeeManagementDesktop.invoiceSent'), sublabel: 'Automatic invoice confirmations' },
        ]}
        multiValue={(Object.keys(draftCategories) as (keyof NotificationCategories)[]).filter(k => draftCategories[k])}
        onMultiToggle={(val) => setDraftCategories(prev => ({ ...prev, [val]: !prev[val as keyof NotificationCategories] }))}
        onSave={handleSaveCategories}
      />
    </IonPage>
  );
};

export default EmployeeManagementDesktop;
